import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Turns URLs inside plain text into anchors that open in a new tab, and phone
 * numbers into tel: anchors that dial in one tap (ENG-2196).
 *
 * Ported from the web widget's tested linkify module
 * (rosie-web-widget/src/services/linkify.ts) — keep the matching rules in sync:
 *
 * - https:// and www. URLs always link,
 * - bare domains with a path or query link with any plausible TLD
 *   ("ubreakifix.com/locations/seattle?utm_source=..."),
 * - bare domains alone link only with a common TLD, so "report.txt" in prose
 *   stays text,
 * - the domain half of an email address never links,
 * - trailing sentence punctuation stays outside the anchor,
 * - North American phone numbers (+1 / 1 optional, 3-3-4 digits, any of the
 *   usual separators or none) dial as tel:+1… and display as "(503) 765-6418"
 *   however they were written,
 * - other international numbers with a leading + and 9–15 digits dial as
 *   written,
 * - seven-digit local numbers, dates, prices, card numbers and order numbers
 *   glued to '#' or a word stay text. URLs are split out first, so digits
 *   inside a URL are never re-matched as a phone.
 *
 * No regex lookbehind (older Safari throws at parse time), and all non-link
 * text is HTML-escaped before the trusted HTML is assembled.
 */

const COMMON_TLDS = 'com|net|org|io|co|ai|app|dev|me|us|ca|uk|info|biz|shop|store|site|online';

const URL_PATTERN = new RegExp(
  '(' +
    'https?://[^\\s<]+|www\\.[^\\s<]+' +
    '|' +
    '\\b[a-z0-9][a-z0-9-]*(?:\\.[a-z0-9-]+)*\\.[a-z]{2,24}(?:/[^\\s<]*|\\?[^\\s<]*)' +
    '|' +
    `\\b[a-z0-9][a-z0-9-]*(?:\\.[a-z0-9-]+)*\\.(?:${COMMON_TLDS})\\b` +
    ')',
  'gi'
);

const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/;

/** Space, dot or dash between the groups of a phone number — or nothing. */
const PHONE_SEPARATOR = '[\\s.-]?';

/**
 * North American Numbering Plan: optional +1 / 1, then a 3-digit area code
 * (optionally in parentheses), a 3-digit exchange and a 4-digit line number.
 * Area codes and exchanges never start with 0 or 1, which rules out most dates
 * and card numbers.
 */
const NANP_PATTERN =
  `(?:\\+?1${PHONE_SEPARATOR})?` +
  `(?:\\(\\s?[2-9]\\d{2}\\s?\\)|[2-9]\\d{2})${PHONE_SEPARATOR}` +
  `[2-9]\\d{2}${PHONE_SEPARATOR}\\d{4}`;

/** Any other country: + and a country code, then 2–6 groups of 1–4 digits. */
const INTERNATIONAL_PATTERN = `\\+\\d{1,3}(?:${PHONE_SEPARATOR}\\(\\d{1,4}\\)|${PHONE_SEPARATOR}\\d{1,4}){2,6}`;

const PHONE_PATTERN = new RegExp(`(?:(${NANP_PATTERN})|(${INTERNATIONAL_PATTERN}))(?!\\d)`, 'g');

/** A number glued to one of these is an order number, a price, a date… not a phone. */
const PHONE_ATTACHES_TO_PREVIOUS = /[\w#$@+/.-]/;

const INTERNATIONAL_MIN_DIGITS = 9;
const INTERNATIONAL_MAX_DIGITS = 15; // E.164 upper bound

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

@Pipe({
  name: 'linkify',
  standalone: false
})
export class LinkifyPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(linkifyToHtml(value ?? ''));
  }
}

/** Exported separately so the transformation is testable without Angular DI. */
export function linkifyToHtml(content: string): string {
  const parts: string[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  URL_PATTERN.lastIndex = 0;

  while ((match = URL_PATTERN.exec(content)) !== null) {
    const matchStart = match.index;
    let url = match[0];

    const hasExplicitPrefix = /^(https?:\/\/|www\.)/i.test(url);
    const precededBy = matchStart > 0 ? content[matchStart - 1] : '';

    // Bare-domain matches inside emails or words stay text.
    if (!hasExplicitPrefix && /[@\w.-]/.test(precededBy)) {
      continue;
    }

    if (matchStart > lastIndex) {
      parts.push(linkifyPhonesToHtml(content.slice(lastIndex, matchStart)));
    }

    let trailing = '';
    const trailingMatch = url.match(TRAILING_PUNCTUATION);
    if (trailingMatch) {
      trailing = trailingMatch[0];
      url = url.slice(0, url.length - trailing.length);
    }

    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    parts.push(
      `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(url)}</a>`
    );

    if (trailing) {
      parts.push(escapeHtml(trailing));
    }

    lastIndex = matchStart + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push(linkifyPhonesToHtml(content.slice(lastIndex)));
  }

  return parts.join('');
}

/** Escapes a run of non-URL text, turning phone numbers into tel: anchors. */
function linkifyPhonesToHtml(content: string): string {
  const parts: string[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  PHONE_PATTERN.lastIndex = 0;

  while ((match = PHONE_PATTERN.exec(content)) !== null) {
    const matchStart = match.index;
    const precededBy = matchStart > 0 ? content[matchStart - 1] : '';

    if (PHONE_ATTACHES_TO_PREVIOUS.test(precededBy)) {
      continue;
    }

    const phone = match[1] ? formatNanp(match[0]) : formatInternational(match[0]);
    if (!phone) {
      continue;
    }

    if (matchStart > lastIndex) {
      parts.push(escapeHtml(content.slice(lastIndex, matchStart)));
    }

    parts.push(`<a href="${escapeHtml(phone.href)}">${escapeHtml(phone.text)}</a>`);
    lastIndex = matchStart + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push(escapeHtml(content.slice(lastIndex)));
  }

  return parts.join('');
}

function formatNanp(raw: string): { text: string; href: string } {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 11) {
    digits = digits.slice(1); // drop the leading country code
  }
  return {
    text: `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`,
    href: `tel:+1${digits}`
  };
}

function formatInternational(raw: string): { text: string; href: string } | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length < INTERNATIONAL_MIN_DIGITS || digits.length > INTERNATIONAL_MAX_DIGITS) {
    return null;
  }
  return { text: raw, href: `tel:+${digits}` };
}
